import classnames from 'classnames';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import isItemView from 'mini-mail-list-store/lib/utils/isItemView';
import loadAutomaticRepliesBannerOptions from 'mini-mail-list-store/lib/actions/loadAutomaticRepliesBannerOptions';
import React, { useEffect } from 'react';
import setMailListFocusInfo from 'mini-mail-list-store/lib/actions/setMailListFocusInfo';
import setMultiSelectPerfDatapoint from 'mini-mail-list-store/lib/actions/multiSelect/setMultiSelectPerfDatapoint';
import styles from './MailSurfaceBody.scss';
import { canShowUpNext } from 'mini-mail-list-view/lib/utils/mailList/canShowUpNext';
import { generatePath, useNavigate } from 'mini-react-router-dom';
import { getItem, removeItem } from 'mini-localstorage';
import { isPWA } from 'mini-device-utils/lib/utils/isPWA';
import { isPWAInstalled } from 'mini-device-utils/lib/utils/isPWAInstalled';
import { LazyPWANudgeBottomSheet } from 'mini-a2hs';
import { LazyUpNextEventDetailRoutes } from 'mini-mail-list-upnext-view';
import { MAIL_LIST_SCROLLED_3_TIMES } from 'mini-workflow-tracking/lib/constants/ListViewWorkflowConstants';
import { MailList } from 'mini-mail-list-view';
import { MailListViewBehaviorProvider } from 'mini-mail-list-behavior-connect/lib/context-providers/MailListViewBehaviorProvider';
import { type MailRoutes } from 'mini-routes';
import { observer } from 'mobx-react-lite';
import { shouldShowThemeHeroImage } from 'mini-mail-list-view/lib/utils/mailList/shouldShowThemeHeroImage';
import { SHOW_PWA_NUDGE } from 'mini-a2hs/lib/schema/constants';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { UpNextStateContextProvider } from 'mini-upnext/lib/components/UpNextStateContext';

// Used to signal that the mail list has been scrolled 3 times once per session
let onMailListScrolling: boolean = false;
let mailListScrolledCount: number = 0;

export const MailSurfaceBody = observer(function MailSurfaceBody() {
    const [showPWANudgeBottomSheet, setShowPWANudgeBottomSheet] = React.useState(false);
    const tableView = getSelectedTableView();
    const { isInCheckedMode, multiSelectPerfDatapoint } = tableView ?? {};
    const showUpNext = canShowUpNext();
    const showThemeHeroImage = shouldShowThemeHeroImage();
    const navigate = useNavigate();

    useEffect(() => {
        // Load the auto reply config to store
        loadAutomaticRepliesBannerOptions();
    }, []);

    useEffect(() => {
        // Show PWA nudge bottom sheet if the app is installed but nudge is not shown on re-render of mail surface body
        const showPWANudge = getItem(SHOW_PWA_NUDGE);
        if (showPWANudge) {
            if (isPWA()) {
                // Clear the flag if user is already in PWA
                removeItem(SHOW_PWA_NUDGE);
            } else {
                isPWAInstalled().then(isInstalled => {
                    if (isInstalled) {
                        removeItem(SHOW_PWA_NUDGE);
                        setShowPWANudgeBottomSheet(true);
                    }
                });
            }
        }
    });

    useEffect(() => {
        if (isInCheckedMode && multiSelectPerfDatapoint) {
            multiSelectPerfDatapoint.end();
            setMultiSelectPerfDatapoint(null);
        }
    }, [isInCheckedMode, multiSelectPerfDatapoint]);

    useEffect(() => () => {
        setMailListFocusInfo(getSelectedTableView(), {
            shouldFocusInMailList: false,
            focusedRowId: null,
        });
    });

    const onMailListScroll = () => {
        onMailListScrolling = true;
    };

    const onMailListTouchEnd = () => {
        if (onMailListScrolling && mailListScrolledCount < 3) {
            mailListScrolledCount++;
            if (mailListScrolledCount === 3) {
                signalWorkflowEvent(MAIL_LIST_SCROLLED_3_TIMES);
            }
        }
    };

    const onMailListItemClick = React.useCallback(
        (rowId: string) => {
            const encodedId = encodeURIComponent(rowId);
            isItemView()
                ? navigate(generatePath('itemId/:id', { id: encodedId }))
                : navigate(
                      generatePath('id/:id', {
                          id: encodedId,
                      })
                  );
        },
        [navigate]
    );

    const mailListViewBehaviour = React.useMemo(
        () => ({ onMailListItemClick }),
        [onMailListItemClick]
    );

    const closePWANudgeBottomSheet = () => {
        setShowPWANudgeBottomSheet(false);
    };

    return (
        <UpNextStateContextProvider>
            <main
                className={classnames(styles.listPane, showThemeHeroImage && styles.themeHeroImage)}
                onScroll={onMailListScroll}
                onTouchEnd={onMailListTouchEnd}>
                <MailListViewBehaviorProvider value={mailListViewBehaviour}>
                    <MailList tableView={tableView} />
                </MailListViewBehaviorProvider>
            </main>

            {showUpNext && <LazyUpNextEventDetailRoutes />}

            <LazyPWANudgeBottomSheet
                showPWANudgeBottomSheet={showPWANudgeBottomSheet}
                closePWANudgeBottomSheet={closePWANudgeBottomSheet}
            />
        </UpNextStateContextProvider>
    );
});
